import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDemo } from "@/context/DemoContext";
import { eur, pct, num, STATE_COLORS } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";
import { TrendingUp, CheckCircle2, Layers, Loader2, Wallet, Target, Gauge, FileText, Gavel, Sparkles, Clock, Activity } from "lucide-react";
import { PRIORITY } from "@/lib/format";

const STRAT_COLORS = ["#EA7B2B", "#10B981", "#3B82F6", "#8B5CF6", "#F59E0B", "#6366F1", "#E11D48", "#0EA5E9"];

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5 hover:border-white/20 transition-colors" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent || "#71717A" }} />
      </div>
      <div className="mt-3 font-mono text-2xl text-white truncate">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5" data-testid={testid}>
      <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider mb-5">{title}</h2>
      {children}
    </div>
  );
}

const tooltipStyle = {
  backgroundColor: "#0A1826",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "2px",
  fontSize: "12px",
  color: "#fff",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { showDemo } = useDemo();

  useEffect(() => {
    api.get("/dashboard", { params: { include_demo: showDemo } }).then((r) => setData(r.data)).catch(() => {});
  }, [showDemo]);

  if (!data)
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" />
      </div>
    );

  const k = data.kpi;
  const stateData = data.by_state.filter((s) => s.count > 0);
  const stratData = data.by_strategy.filter((s) => s.count > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-light text-white">Centro di comando</h1>
        <p className="text-sm text-zinc-500 mt-1">Executive summary · {num(k.totale)} operazioni monitorate</p>
      </div>

      {/* Priority strip */}
      {data.priority && (
        <div className="flex flex-wrap items-center gap-3" data-testid="priority-strip">
          {["alta", "media", "bassa"].map((lv) => (
            <div key={lv} className="flex items-center gap-2 bg-[#122A3F] border border-white/10 rounded-sm px-4 py-2.5" data-testid={`priority-count-${lv}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY[lv].color }} />
              <span className="font-mono text-lg text-white">{num(data.priority[lv] || 0)}</span>
              <span className="text-xs text-zinc-500 uppercase tracking-wider">{PRIORITY[lv].label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi label="Operazioni analizzate" value={num(k.totale)} sub={`${num(k.scartate)} scartate`} icon={Layers} accent="#3B82F6" />
        <Kpi label="Operazioni qualificate" value={num(k.qualificate)} sub={`${pct(k.tasso_qualifica)} tasso qualifica`} icon={CheckCircle2} accent="#10B981" />
        <Kpi label="Pipeline potenziale" value={eur(k.pipeline_value)} sub="margine netto aggregato" icon={Wallet} accent="#EA7B2B" />
        <Kpi label="Valore perizia totale" value={eur(k.valore_perizia_totale)} sub="stima peritale aggregata" icon={FileText} accent="#8B5CF6" />
        <Kpi label="Valore base asta" value={eur(k.valore_base_totale)} sub="prezzi base aggregati" icon={Gavel} accent="#0EA5E9" />
        <Kpi label="ROI medio" value={pct(k.roi_medio)} sub="pipeline qualificata" icon={TrendingUp} accent="#EA7B2B" />
        <Kpi label="Confidence medio" value={num(k.confidence_medio)} sub="affidabilità dati" icon={Gauge} accent="#10B981" />
        <Kpi label="Completezza media" value={pct(k.completezza_media)} sub="dati raccolti" icon={Target} accent="#F59E0B" />
        <Kpi label="Nuove (24h)" value={num(k.nuove_24h)} sub="acquisite nelle ultime 24h" icon={Sparkles} accent="#22C55E" />
        <Kpi label="Variazioni (24h)" value={num(k.variazioni_24h)} sub="modifiche rilevate" icon={Activity} accent="#E11D48" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Pipeline per stato workflow" testid="chart-pipeline">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stateData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <XAxis type="number" stroke="#52525b" fontSize={11} />
                <YAxis type="category" dataKey="stato" stroke="#a1a1aa" fontSize={11} width={130} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count" radius={[0, 2, 2, 0]} barSize={18}>
                  {stateData.map((e, i) => (
                    <Cell key={i} fill={STATE_COLORS[e.stato] || "#EA7B2B"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Distribuzione score" testid="chart-score">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.score_distribution} margin={{ left: -10 }}>
              <XAxis dataKey="fascia" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#52525b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]} barSize={40}>
                {data.score_distribution.map((e, i) => (
                  <Cell key={i} fill={["#71717A", "#D97706", "#EA7B2B", "#10B981"][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Operazioni per strategia" testid="chart-strategy-count">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={stratData} dataKey="count" nameKey="strategia" cx="40%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {stratData.map((e, i) => (
                  <Cell key={i} fill={STRAT_COLORS[i % STRAT_COLORS.length]} stroke="#0A1826" />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: "11px", color: "#a1a1aa" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Valore pipeline per strategia" testid="chart-strategy-value">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stratData} margin={{ left: 10, bottom: 40 }}>
              <XAxis dataKey="strategia" stroke="#a1a1aa" fontSize={10} angle={-25} textAnchor="end" interval={0} height={60} />
              <YAxis stroke="#52525b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => eur(v)} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]} barSize={30}>
                {stratData.map((e, i) => (
                  <Cell key={i} fill={STRAT_COLORS[i % STRAT_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
